/* eslint-disable no-extend-native */
declare global {
	interface Array<T> {
		/**
		 * Returns the last element, or throws an error if the array is empty.
		 */
		last(): T;
		
		/**
		 * Returns the first element, or throws an error if the array is empty.
		 */
		first(): T;
		
		/**
		 * Returns the last element, or null if the array is empty.
		 */
		lastOrNull(): T | null;
		
		/**
		 * Returns the first element, or null if the array is empty.
		 */
		firstOrNull(): T | null;
		
		/**
		 * Returns a {@link Map} containing the values provided by valueSelector and indexed by keySelector functions applied to elements of the given array.
		 *
		 * If any two elements would have the same key returned by keySelector the last one gets added to the map.
		 *
		 * @param keySelector a function to select keys from an element.
		 * @param valueSelector a function to select values from an element. If no valueSelector is present, the element itself will be added instead.
		 */
		associateBy<K, V = T>(keySelector: (item: T) => K, valueSelector?: (item: T) => V): Map<K, V>;
		
		/**
		 * Returns a {@link Map} containing the values provided by valueSelector and indexed by keySelector functions applied to elements of the given array.
		 *
		 * If multiple elements would have the same key returned by keySelector they are all included in the values.
		 * The value arrays retain the order of items in the initial array.
		 *
		 * @param keySelector a function to select keys from an element.
		 * @param valueSelector a function to select values from an element. If no valueSelector is present, the element itself will be added instead.
		 */
		associateManyBy<K, V = T>(keySelector: (item: T) => K, valueSelector?: (item: T) => V): Map<K, V[]>;
		
		sum(): number;
	}
}

Array.prototype.last = function <T>(): T {
	return this[this.length - 1];
};

Array.prototype.first = function <T>(): T {
	return this[0];
};

Array.prototype.lastOrNull = function <T>(): T | null {
	if (this.length === 0)
		return null;
	else
		return this[this.length - 1];
};

Array.prototype.firstOrNull = function <T>(): T | null {
	if (this.length === 0)
		return null;
	else
		return this[0];
};

Array.prototype.associateBy = function <T, K, V>(
	keySelector: (item: T) => K,
	valueSelector?: (item: T) => V,
): Map<K, V> {
	const map = new Map<K, V>();
	if (valueSelector)
		this.forEach(it => map.set(keySelector(it), valueSelector(it)));
	else
		this.forEach(it => map.set(keySelector(it), it));
	return map;
};

Array.prototype.associateManyBy = function <T, K, V>(
	keySelector: (item: T) => K,
	valueSelector?: (item: T) => V,
): Map<K, V[]> {
	const map = new Map<K, V[]>();
	this.forEach(it => {
		const key = keySelector(it);
		
		let items = map.get(key);
		if (items === undefined) {
			items = [];
			map.set(key, items);
		}
		if (valueSelector)
			items.push(valueSelector(it));
		else
			items.push(it);
	});
	return map;
};

Array.prototype.sum = function() {
	return this.reduce((previousValue, currentValue) => previousValue + currentValue, 0);
};

export const arrayExtensions = {
	load: () => ({}),
};
